import { useGetWorkspaceById } from "@/api/workspace";
import { defineStore } from "pinia";
import { useSessionStore } from "./session.store";
import { computed, ref } from "vue";
import {
  PermissionHelper,
  type PermissionGroup,
  type PermissionResult
} from "@territoriesai/types/lib/helpers/permission.helper.mjs";
import { useGetBillingPlanById } from "@/api/billing-plan";

export interface PermissionConfig {
  resource: string;
  operation: string;
  currentResourceCount?: number;
  requestedResourceCount?: number;
  creditsRequired?: number;
}

export interface PermissionConfigWithOperator {
  permissions: PermissionConfig[];
  operator: "AND" | "OR";
}

export const usePermissionStore = defineStore("permission", () => {
  const sessionStore = useSessionStore();
  const workspaceId = ref(
    sessionStore.selectedSession()?.workspaceUserGroup.workspaceId as string
  );
  const workspaceUserGroup = sessionStore.selectedSession()?.workspaceUserGroup;
  const { data: currentWorkspaceData, isFetching } =
    useGetWorkspaceById(workspaceId);
  const billingPlanId = computed(
    () => currentWorkspaceData.value?.workspace.currentBillingPlanId as string
  );
  const { data: billingPlan } = useGetBillingPlanById(billingPlanId);

  const checkSinglePermission = (
    permissionConfig: PermissionConfig
  ): PermissionResult => {
    return PermissionHelper.hasPermission(
      workspaceUserGroup?.group as PermissionGroup,
      permissionConfig.resource,
      permissionConfig.operation,
      billingPlan.value,
      permissionConfig.currentResourceCount,
      permissionConfig.requestedResourceCount,
      currentWorkspaceData.value?.workspace,
      permissionConfig.creditsRequired
    );
  };

  const hasPermission = (
    config: PermissionConfig | PermissionConfig[] | PermissionConfigWithOperator
  ): PermissionResult => {
    // Handle new format with explicit operator
    if (
      typeof config === "object" &&
      "permissions" in config &&
      "operator" in config
    ) {
      const results = config.permissions.map(checkSinglePermission);

      if (config.operator === "OR") {
        const allowed = results.some((result) => result.allowed);
        return {
          allowed,
          denyCategory: allowed ? undefined : results[0]?.denyCategory,
          denyReason: allowed ? undefined : results[0]?.denyReason
        } as PermissionResult;
      }

      // AND logic (default)
      const allowed = results.every((result) => result.allowed);
      return {
        allowed,
        denyCategory: results.find((result) => !result.allowed)?.denyCategory,
        denyReason: results.find((result) => !result.denyReason)?.denyReason
      } as PermissionResult;
    }

    // Handle legacy array format (AND logic)
    if (Array.isArray(config)) {
      const results = config.map(checkSinglePermission);

      const allowed = results.every((result) => result.allowed);
      return {
        allowed,
        denyCategory: results.find((result) => !result.allowed)?.denyCategory,
        denyReason: results.find((result) => !result.denyReason)?.denyReason
      } as PermissionResult;
    }

    // Handle single permission
    return checkSinglePermission(config);
  };

  return {
    hasPermission,
    isFetching
  };
});
